import type { ReactNode } from 'react'

/**
 * Иконки для разделов «Базы знаний».
 * Стиль — Heroicons outline, тонкие линии, stroke=currentColor.
 * Совпадает с иконками в Layout.tsx (сайдбар) — единая визуальная система
 * вместо разнокалиберных эмодзи.
 */

export type KBIconName =
  | 'dashboard'
  | 'clients'
  | 'requests'
  | 'kp'
  | 'table'
  | 'settings'
  | 'queue'
  | 'excel'
  | 'quotes'
  | 'accounts'
  | 'rates'
  | 'catalog'
  | 'bell'
  | 'roles'
  | 'mail'
  | 'orders'
  | 'suppliers'
  | 'analytics'
  | 'planning'

function SvgIcon({ children, size = 18 }: { children: ReactNode; size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="flex-shrink-0"
    >
      {children}
    </svg>
  )
}

const ICON_PATHS: Record<KBIconName, ReactNode> = {
  // Дашборд — 4 плитки
  dashboard: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </>
  ),
  // Клиенты — группа людей
  clients: (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ),
  // Заявки — документ со строками
  requests: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </>
  ),
  // КП — документ со значком валюты (₽ упрощённо — вертикальная линия+горизонт)
  kp: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M9 18v-7h3.5a2.5 2.5 0 0 1 0 5H9" />
      <line x1="7" y1="15" x2="13" y2="15" />
    </>
  ),
  // Таблица — три столбца
  table: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="9" x2="9" y2="21" />
    </>
  ),
  // Настройки — шестерёнка
  settings: (
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </>
  ),
  // Очередь закупки — тележка
  queue: (
    <>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </>
  ),
  // Excel-workflow — документ со стрелкой вниз (импорт/скачать)
  excel: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <polyline points="9 13 12 16 15 13" />
      <line x1="12" y1="16" x2="12" y2="11" />
    </>
  ),
  // Квоты — ценник/тег
  quotes: (
    <>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <circle cx="7" cy="7" r="1.5" />
    </>
  ),
  // Аккаунты — человек в кружке (админ)
  accounts: (
    <>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </>
  ),
  // Курсы валют — круг со стрелками обмена
  rates: (
    <>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 12h8M14 9l3 3-3 3" />
      <path d="M16 12H8M10 15l-3-3 3-3" />
    </>
  ),
  // Каталог — ящик с полками
  catalog: (
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
      <path d="M10 3v18" />
    </>
  ),
  // Колокольчик уведомлений
  bell: (
    <>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </>
  ),
  // Роли — щит/значок
  roles: (
    <>
      <path d="M12 2L4 6v6c0 5.5 3.5 10 8 10s8-4.5 8-10V6l-8-4z" />
      <path d="M9 12l2 2 4-4" />
    </>
  ),
  // Письмо/конверт — для запросов поставщикам
  mail: (
    <>
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="M22 7l-10 6L2 7" />
    </>
  ),
  // Заказы — гроссбух (как в сайдбаре)
  orders: (
    <>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1Z" />
      <line x1="8" y1="8" x2="16" y2="8" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </>
  ),
  // Поставщики — грузовик (как в сайдбаре)
  suppliers: (
    <>
      <rect x="1" y="3" width="15" height="13" rx="1" />
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8" />
      <circle cx="5.5" cy="18.5" r="2" />
      <circle cx="18.5" cy="18.5" r="2" />
    </>
  ),
  // Аналитика — столбцы (как в сайдбаре)
  analytics: (
    <>
      <rect x="3" y="12" width="4" height="9" rx="1" />
      <rect x="10" y="7" width="4" height="14" rx="1" />
      <rect x="17" y="3" width="4" height="18" rx="1" />
    </>
  ),
  // Планирование — растущий график (как в сайдбаре)
  planning: (
    <>
      <line x1="3" y1="21" x2="21" y2="21" />
      <polyline points="3 14 8 9 12 13 21 4" />
      <polyline points="21 9 21 4 16 4" />
    </>
  ),
}

export function KBIcon({ name, size = 18 }: { name: KBIconName; size?: number }) {
  return <SvgIcon size={size}>{ICON_PATHS[name]}</SvgIcon>
}
